import { useMemo, useState } from 'react';
import { Modal } from '../../components/UI';
import { displayDate } from '../../utils/date';
import { canDismissNotification, filterNotifications, type NotificationTab } from '../../utils/notifications';
import type { AppNotification, NotificationActionTarget } from '../../types';

const tabs: Array<{ key: NotificationTab; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'critical', label: 'Critical' },
  { key: 'payments', label: 'Payments' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'other', label: 'Other' }
];

const priorityLabel = (priority: AppNotification['priority']) => priority === 'medium' ? 'normal' : priority;
const dateLabel = (item: AppNotification) => item.dueDate ? `Due ${displayDate(item.dueDate)}` : item.dateISO ? displayDate(item.dateISO) : '';

export function NotificationCenter({ notifications, unreadCount, loading = false, open: controlledOpen, onOpenChange, onRead, onReadAll, onDismiss, onAction, onRefresh }: {
  notifications: AppNotification[];
  unreadCount: number;
  loading?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onRead: (id: string) => Promise<unknown>;
  onReadAll: () => Promise<unknown>;
  onDismiss: (item: AppNotification) => Promise<unknown>;
  onAction: (target: NotificationActionTarget, item: AppNotification) => void;
  onRefresh: () => Promise<unknown>;
}) {
  const [localOpen, setLocalOpen] = useState(false);
  const [tab, setTab] = useState<NotificationTab>('all');
  const [working, setWorking] = useState('');
  const open = controlledOpen ?? localOpen;
  const setOpen = onOpenChange ?? setLocalOpen;
  const visible = useMemo(() => filterNotifications(notifications, tab), [notifications, tab]);
  const criticalCount = notifications.filter((item) => item.priority === 'critical').length;

  const act = async (key: string, fn: () => Promise<unknown>) => {
    if (working) return;
    setWorking(key);
    try {
      await fn();
    } finally {
      setWorking('');
    }
  };

  const openAction = async (item: AppNotification) => {
    const target = item.action?.target;
    await act(`read:${item.dedupeKey || item.id}`, () => onRead(item.dedupeKey || item.id));
    if (target) {
      onAction(target, item);
      setOpen(false);
    }
  };

  return <>
    <button className="bell-btn" onClick={() => setOpen(true)} aria-label={`${unreadCount} unread notifications`}>
      <span aria-hidden="true">!</span>
      {unreadCount > 0 && <b aria-live="polite">{unreadCount > 9 ? '9+' : unreadCount}</b>}
    </button>
    <Modal open={open} title="Notifications" onClose={() => setOpen(false)}>
      <div className="notification-head">
        <div>
          <p className="muted">Payments, account alerts, reconciliation issues, and important reminders.</p>
          <strong>{unreadCount} unread{criticalCount ? ` / ${criticalCount} critical` : ''}</strong>
        </div>
        <div className="notification-head-actions">
          <button className="btn small" disabled={loading || !!working} onClick={() => act('refresh', onRefresh)}>{loading || working === 'refresh' ? 'Refreshing...' : 'Refresh'}</button>
          <button className="btn small" disabled={!unreadCount || !!working} onClick={() => act('all', onReadAll)}>Mark all read</button>
        </div>
      </div>
      <div className="notification-tabs" role="tablist" aria-label="Notification filters">
        {tabs.map((item) => <button key={item.key} role="tab" aria-selected={tab === item.key} className={tab === item.key ? 'active' : ''} onClick={() => setTab(item.key)}>{item.label}</button>)}
      </div>
      <div className="notification-list">
        {visible.map((item) => <article className={`notification-row ${item.status} priority-${priorityLabel(item.priority)}`} key={item.dedupeKey || item.id}>
          <div className="notification-marker" aria-hidden="true">{String(item.category || item.type || 'system').slice(0, 1).toUpperCase()}</div>
          <div className="notification-copy">
            <div className="notification-title-line"><b>{item.title}</b><span className={`priority-pill ${priorityLabel(item.priority)}`}>{priorityLabel(item.priority)}</span>{item.status === 'unread' && <span className="unread-dot">Unread</span>}</div>
            <span>{item.message}</span>
            <small>{[dateLabel(item), item.source].filter(Boolean).join(' / ')}</small>
          </div>
          <div className="notification-actions">
            {item.action && <button className="btn small primary" disabled={!!working} onClick={() => openAction(item)}>{item.action.label}</button>}
            {item.status === 'unread' && <button className="btn small" disabled={!!working} onClick={() => act(`read:${item.dedupeKey || item.id}`, () => onRead(item.dedupeKey || item.id))}>Mark read</button>}
            {canDismissNotification(item) ? <button className="btn small ghost" disabled={!!working} onClick={() => act(`dismiss:${item.dedupeKey || item.id}`, () => onDismiss(item))}>Dismiss</button> : <small className="locked-note">Visible until fixed</small>}
          </div>
        </article>)}
      </div>
      {!visible.length && <p className="empty-state">No notifications in this view.</p>}
    </Modal>
  </>;
}
